import { PaginationParams } from '../types/PaginationObject';

const DEFAULT_TAKE = 15;
const DEFAULT_PAGE = 1;

export type Pagination = {
  total: number;
  hasMore: boolean;
  nextPage: number | null;
  previousPage?: number | null;
};

export function getPageNumbers(
  currentPage: number,
  totalPages: number
): number[] {
  if (totalPages <= 1) {
    return [1];
  }
  const delta = 2;
  const range: number[] = [];
  const result: number[] = [];

  for (
    let pageNumber = Math.max(2, currentPage - delta);
    pageNumber <= Math.min(totalPages - 1, currentPage + delta);
    pageNumber += 1
  ) {
    range.push(pageNumber);
  }

  result.push(1);
  if (currentPage - delta > 3) {
    result.push(-1);
  }
  range.forEach(pageNumber => result.push(pageNumber));
  if (currentPage + delta < totalPages - 2) {
    result.push(-1);
  }
  if (totalPages > 1) {
    result.push(totalPages);
  }
  return result;
}

type UsePaginationProps = {
  pagination?: Pagination;
  queryParams: PaginationParams;
  onQueryParamsChange: (queryParams: Partial<PaginationParams>) => void;
};

export function usePagination({
  queryParams,
  pagination,
  onQueryParamsChange
}: UsePaginationProps) {
  const currentPage1Based = queryParams?.page ?? DEFAULT_PAGE;
  const take = queryParams?.limit ?? DEFAULT_TAKE;

  const totalPageCount = (() => {
    if (!pagination?.total) {
      return 0;
    }
    return Math.ceil(pagination.total / take) || 1;
  })();

  const pageNumbers =
    totalPageCount > 0 ? getPageNumbers(currentPage1Based, totalPageCount) : [];

  const prevPage = pagination?.previousPage ?? null;
  const nextPage = pagination?.nextPage ?? null;
  const hasNext = pagination?.hasMore ?? false;
  const hasPrev = prevPage != null;

  const goToPage = (page: number) => {
    onQueryParamsChange({ page });
  };

  const handlePageChange = () => {
    onQueryParamsChange({
      page: nextPage ?? DEFAULT_PAGE
    });
  };

  const goToPrevious = () => {
    if (prevPage != null) {
      onQueryParamsChange({ page: prevPage });
    }
  };

  return {
    totalPageCount,
    currentPage: currentPage1Based - 1,
    currentPage1Based,
    totalCount: pagination?.total ?? 0,
    handlePageChange,
    pageNumbers,
    goToPage,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    goToPrevious
  };
}
